/**
 * Gathering the evidence base once, for the intelligence report and for the
 * brief built on it.
 *
 * Every input is optional and named when absent: a source the platform does
 * not have is a gap the report states, not a failure of the whole page.
 */
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../../app/SessionContext";
import type { DataSource } from "../../api/dataSource";
import type { AlertExplanation } from "../../domain/alerts";
import { buildIntelligenceReport, type IntelligenceReport, type ReportInputs } from "../../domain/intelligence";
import type { Fit } from "../../domain/reliability";
import type { WorkOrder } from "../../domain/workOrders";
import { t } from "../../i18n";

export interface Gathered {
  inputs: ReportInputs;
  report: IntelligenceReport;
}

export async function gatherIntelligence(source: DataSource, siteId: string, siteCode: string): Promise<Gathered> {
  const gaps: string[] = [];
  const take = async <T>(what: string, p: Promise<T>, fallback: T): Promise<T> => {
    try {
      return await p;
    } catch (e) {
      gaps.push(`${what}: ${e instanceof Error ? e.message : String(e)}`);
      return fallback;
    }
  };
  const [alerts, rules, work, testing, compliance, liability, populations, drift, facts, holdPoints] =
    await Promise.all([
      take(t("alerts"), source.listAlerts(siteId), []),
      take(t("rules"), source.listRules(), []),
      take(
        t("work orders"),
        source.listWorkOrders({ siteId }).then((page) => page.items),
        [] as WorkOrder[],
      ),
      take(t("testing summary"), source.getTestingSummary(siteId), null),
      take(t("compliance"), source.getCompliance(siteId), null),
      take(t("deferred-cost register"), source.liabilityRegister(siteId), null),
      take(t("reliability populations"), source.populations(siteId), []),
      take(t("drift checks"), source.driftChecks(siteId), []),
      take(t("records ledger"), source.ledgerFacts(siteId), []),
      take(t("hold points"), source.listHoldPoints(siteId), null),
    ]);
  const explanations: Record<string, AlertExplanation | undefined> = {};
  await Promise.all(
    alerts
      .filter((a) => a.status === "open" || a.status === "acknowledged")
      .map(async (a) => {
        explanations[a.id] = await take(t("explanation of {id}", { id: a.id }), source.explainAlert(a.id), undefined);
      }),
  );
  const fits: Record<string, Fit[]> = {};
  await Promise.all(
    populations
      .filter((p) => p.fittable)
      .map(async (p) => {
        fits[p.population] = await take(
          t("fit for {population}", { population: p.population }),
          source.fits(siteId, p.population),
          [],
        );
      }),
  );
  const ledger = await Promise.all(
    facts
      .filter((f) => f.partyKeys.length === 2)
      .map(async (fact) => ({
        fact,
        divergence: await take(
          t("divergence on {fact}", { fact: fact.factKey }),
          source.divergence({ siteId, factKey: fact.factKey, partyA: fact.partyKeys[0], partyB: fact.partyKeys[1] }),
          null,
        ),
      })),
  );
  const inputs: ReportInputs = {
    now: new Date(),
    siteCode,
    alerts,
    explanations,
    rules,
    workOrders: work,
    testing,
    compliance,
    liability,
    populations,
    fits,
    drift,
    ledger,
    holdPoints,
    gaps,
  };
  return { inputs, report: buildIntelligenceReport(inputs) };
}

/**
 * The report is rebuilt from the gathered inputs where it is read, not kept
 * from the fetch: its sentences are in the reader's language, and a change of
 * language remounts the screen without fetching again.
 */
function withReport(g: Gathered): Gathered {
  return { inputs: g.inputs, report: buildIntelligenceReport(g.inputs) };
}

export function useIntelligence() {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  const siteCode = session.site.code;
  return useQuery({
    queryKey: ["intelligence-report", siteId],
    staleTime: 60_000,
    queryFn: () => gatherIntelligence(source, siteId, siteCode),
    select: withReport,
  });
}
